import type { SheetState } from "./schemaDesigner";

// Mirrors the backend validation for BootstrapDataImportProjectV2 per-mapping variantKey.
const VARIANT_KEY_RE = /^[A-Za-z0-9_-]+$/;

/**
 * Per-sheet variantKey inputs, keyed by category instanceId. Only instances
 * reported by `duplicatedCategorySlots` are ever keyed; empty/missing values
 * mean "no explicit variantKey" (the backend groups those mappings under the
 * default key, i.e. today's collapse behavior).
 */
export type SheetVariantKeys = Record<string, Record<string, string>>;

type DuplicatedCategorySlotGroup = {
  categoryId: string;
  categoryName: string;
  instances: Array<{ instanceId: string; instanceLabel: string }>;
};

const effectiveInstance = (sheet: SheetState, column: SheetState["columns"][number]) => ({
  categoryId: column.categoryId ?? sheet.category.category.id,
  categoryName: column.categoryName ?? sheet.category.category.name,
  instanceId: column.categoryInstanceId ?? sheet.category.instanceId,
  instanceLabel: column.categoryInstanceLabel ?? sheet.category.instanceLabel,
});

/**
 * Finds the collapse case within one sheet (= one data source): categories
 * whose property columns come from more than one category instance. Without
 * distinct variantKeys the backend collapses those slots into a single
 * composer (and a single record); the UI offers a variantKey input per
 * instance only for these groups.
 */
function duplicatedCategorySlots(sheet: SheetState): DuplicatedCategorySlotGroup[] {
  const byCategory = new Map<string, DuplicatedCategorySlotGroup>();

  for (const column of sheet.columns) {
    // FK columns become relation mappings, not properties mappings.
    if (column.columnKind === "foreignKey") {
      continue;
    }
    const { categoryId, categoryName, instanceId, instanceLabel } = effectiveInstance(sheet, column);
    const group = byCategory.get(categoryId) ?? { categoryId, categoryName, instances: [] };
    if (!group.instances.some((instance) => instance.instanceId === instanceId)) {
      group.instances.push({ instanceId, instanceLabel });
    }
    byCategory.set(categoryId, group);
  }

  return [...byCategory.values()].filter((group) => group.instances.length > 1);
}

export type VariantKeyValidationError = {
  sheetName: string;
  categoryName: string;
  reason: "incomplete" | "invalidCharset" | "duplicateKey";
};

/**
 * Validates the operator's variantKey inputs against the duplicated slot
 * groups of each sheet. Per group, either every instance is left empty (the
 * slots collapse, today's behavior) or every instance gets a charset-valid
 * ([A-Za-z0-9_-]+), pairwise-distinct key. Returns all violations.
 */
function validateVariantKeys(sheets: SheetState[], variantKeys: SheetVariantKeys): VariantKeyValidationError[] {
  const errors: VariantKeyValidationError[] = [];

  for (const sheet of sheets) {
    const sheetKeys = variantKeys[sheet.name] ?? {};
    for (const group of duplicatedCategorySlots(sheet)) {
      const keys = group.instances.map((instance) => sheetKeys[instance.instanceId]?.trim() ?? "");
      const entered = keys.filter((key) => key !== "");
      if (entered.length === 0) {
        continue;
      }
      if (entered.length < group.instances.length) {
        errors.push({ sheetName: sheet.name, categoryName: group.categoryName, reason: "incomplete" });
        continue;
      }
      if (entered.some((key) => !VARIANT_KEY_RE.test(key))) {
        errors.push({ sheetName: sheet.name, categoryName: group.categoryName, reason: "invalidCharset" });
        continue;
      }
      if (new Set(entered).size !== entered.length) {
        errors.push({ sheetName: sheet.name, categoryName: group.categoryName, reason: "duplicateKey" });
      }
    }
  }

  return errors;
}

/**
 * Resolves the variantKeys to apply for one sheet, keyed by instanceId.
 * Restricted to instances of the sheet's duplicated slot groups so stale
 * inputs (e.g. a group that stopped being duplicated after a column edit)
 * are never sent to the backend.
 */
function applicableVariantKeys(sheet: SheetState, variantKeys: SheetVariantKeys | undefined): Map<string, string> {
  const applicable = new Map<string, string>();
  const sheetKeys = variantKeys?.[sheet.name];
  if (!sheetKeys) {
    return applicable;
  }
  for (const group of duplicatedCategorySlots(sheet)) {
    for (const instance of group.instances) {
      const key = sheetKeys[instance.instanceId]?.trim();
      if (key) {
        applicable.set(instance.instanceId, key);
      }
    }
  }
  return applicable;
}

export const VariantKeySlots = {
  duplicatedCategorySlots,
  validateVariantKeys,
  applicableVariantKeys,
};
